import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View, Image as ReactImage, TouchableOpacity, Modal, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck, faChevronDown, faChevronUp, faLocationDot, faSearchPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { InfoInRow } from '../../InfoInRow';
import DragAndZoom from '../../DragAndZoom';

export default function OccurrenceFormMpMobileAgentPlateDetected({
    resume
}: { resume: MpMobileAgentPlateDetectedResumeData; }) {
    const { styles, theme } = useStyles(styleSheet);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const windowInfo = WindowInformation();
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
    const zoomCanvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = zoomCanvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx || !resume.imageUrl) {
            return;
        }

        setIsImageLoading(true);
        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            setIsImageLoading(false);
        };
        image.onerror = () => {
            setIsImageLoading(false);
        };

        image.src = resume.imageUrl;

    }, [zoomCanvasRef, isModalVisible, resume.imageUrl]);

    return (
        <ScrollView contentContainerStyle={{ rowGap: 10 }}>
            <View>
                <MyAppText style={styles.plateText}>
                    {translate('plate')}
                </MyAppText>
                <MyAppText style={styles.plateInfoText}>
                    {resume.plate}
                </MyAppText>
            </View>
            <Hoverable>
                {({ hovered }) => (<>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }} >
                        <ReactImage
                            style={{
                                width: 360,
                                height: 240,
                                resizeMode: 'contain',
                                borderRadius: 5,
                                overflow: 'hidden',
                            }}
                            source={{ uri: `${resume.imageUrl}` }}
                        />
                    </View>
                    {hovered &&
                        <View style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#00000080',
                        }}>
                            <TouchableOpacity
                                style={{
                                    flex: 1,
                                    justifyContent: 'center'
                                }}
                                onPress={async () => {
                                    setIsModalVisible(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faSearchPlus as IconDefinition} fontSize={28} color={'#FFFFFF'} />
                            </TouchableOpacity>
                        </View>
                    }
                </>)}
            </Hoverable>

            <View style={styles.generalInformation}>
                <TouchableOpacity
                    style={styles.generalInformationDropdown}
                    onPress={() => setShowDetails(!showDetails)}>
                    <MyAppText>{translate('general_info')}</MyAppText>
                    <FontAwesomeIcon icon={(showDetails ? faChevronUp : faChevronDown) as IconDefinition} color={theme.colors.iconColor} />
                </TouchableOpacity>
                <View style={styles.tableLine}></View>
            </View>

            {showDetails ? <>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('vehicleData')}
                    </MyAppText>
                    <InfoInRow title={'model'} value={resume.modelBrand} />
                    <InfoInRow title={'color'} value={resume.color} />
                    <InfoInRow title={'modelYear'} value={resume.modelYear} />
                    <InfoInRow title={'manufacturingYear'} value={resume.manufacturingYear} />
                    <InfoInRow title={'chassis'} value={resume.chassis} />
                </View>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('visualizedOn')}
                    </MyAppText>
                    <InfoInRow value={resume.time} isDate={true} fontAwesomeIcon={faCalendarCheck} />
                    <InfoInRow value={`${resume.location} / ${resume.federativeUnit}`} fontAwesomeIcon={faLocationDot} />
                    <InfoInRow title={'city'} value={resume.city} />
                    <InfoInRow title={'federativeUnit'} value={resume.federativeUnit} />
                </View>
                <InfoInRow title={'description'} value={resume.occurrenceHistory} />
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('systemData')}
                    </MyAppText>
                    <InfoInRow title={'natureOfOccurrence'} value={resume.natureOfOccurrence} />
                    <InfoInRow title={'policeReportNumber'} value={resume.policeReportNumber} />
                    <InfoInRow title={'policeReportYear'} value={resume.policeReportYear} />
                    <InfoInRow title={'policeReportLocation'} value={`${resume.policeReportMunicipality} / ${resume.policeReportFederativeUnit}`} />
                    <InfoInRow title={'occurrenceDate'} value={resume.occurrenceDate} isDate={true} />
                </View>
            </> : <></>}

            {/* Zoom image modal */}
            <Modal
                animationType='fade'
                transparent={true}
                visible={isModalVisible}
                onRequestClose={() => setIsModalVisible(false)}>
                <View style={styles.modalContainer}>
                    <View style={styles.centeredView}>
                        <View style={[styles.modalBody, windowInfo.isMobile ? { width: '95%' } : { maxWidth: '60%' }]}>
                            <View style={{ alignItems: 'flex-end' }}>
                                <TouchableOpacity
                                    style={{ width: 20, height: 20 }}
                                    onPress={() => setIsModalVisible(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes as IconDefinition} style={{ width: 20 }} color={theme.colors.iconColor} />
                                </TouchableOpacity>
                            </View>
                            {isImageLoading && <ActivityIndicator style={[{ width: '100%', height: '100%' }]} size='large' color={theme.colors.loadingColor} />}
                            <DragAndZoom>
                                <canvas style={isImageLoading ? { width: '100%', opacity: 0 } : { width: '100%', opacity: 1 }} ref={zoomCanvasRef} />
                            </DragAndZoom>
                        </View>
                    </View>
                </View>
            </Modal>
        </ScrollView>
    );
}

const styleSheet = createStyleSheet(theme => ({
    container: {
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalBody: {
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 8,
        rowGap: 20,
        padding: 30,
    },
    plateText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    plateInfoText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 24
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
        flex: 1
    },
    generalInformation: {
        flexDirection: 'column',
        paddingHorizontal: 5,
        flex: 1,
        gap: 10
    },
    generalInformationDropdown: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1
    },
}));
